import { AdminViewProvider, useAdminView } from "./admin-view-provider.js";
import {
  AvatarProvider,
  announceAvatarChanged,
} from "../components/avatar-provider.js";
import { AdminViewChoice } from "./admin-view-controls.js";
import { Suspense, useLayoutEffect, useState } from "react";
import type { ClientPageKey, ProductRoute } from "../app/product-router.js";
import type { ProductPageState } from "../app/use-product-page.js";
import type { RoundWorkspace } from "../app/use-round-workspace.js";
import type { PwaStatus } from "../pwa/use-pwa-status.js";
import { isRoundOpen } from "../../domain/round/round-types.js";
import type { BootstrapPayload } from "../../shared/contracts/bootstrap.js";
import type {
  RivalListItem,
  RoundListItem,
} from "../../shared/contracts/product-pages.js";
import {
  RoundDetailDialog,
  ProfileCardOverlay,
  AddRivalDialog,
  RivalryProfileOverlay,
} from "./lazy-overlays.js";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { DesktopLanding } from "./desktop-landing.js";
import { PageBackground } from "./page-background.js";
import { PageContent } from "./page-content.js";
import { ErrorBoundary } from "./error-boundary.js";
import { ApprovedNavigation } from "./approved-navigation.js";
import { useRoundSource } from "./use-round-source.js";
import "../styles/approved-shell.css";

export interface AppShellProps {
  workspace: RoundWorkspace;
  onSignOut(): Promise<void>;
  route: ProductRoute;
  pageState: ProductPageState;
  pwaStatus: PwaStatus;
  requiresAuthentication: boolean;
  authenticated: boolean;
  onAuthenticate?(): void;
}

/**
 * The signed-in application frame: fixed page background, primary
 * navigation, progress hairline and the routed page. Wider than a phone,
 * the frame is one phone screen on a dark stage (shell.css).
 */
export function AppShell(props: AppShellProps): React.JSX.Element {
  return (
    <AdminViewProvider
      accountId={props.workspace.accountId}
      bootstrapAccountId={props.workspace.bootstrap?.account.id ?? null}
      role={props.workspace.bootstrap!.profile.role}
      authenticated={props.authenticated}
    >
      <AvatarProvider
        bootstrap={props.workspace.bootstrap!}
        enabled={props.authenticated}
      >
        <AppShellFrame {...props} />
      </AvatarProvider>
    </AdminViewProvider>
  );
}
function AppShellFrame(props: AppShellProps): React.JSX.Element {
  const { workspace, route } = props;
  usePageScrollReset(route.page);
  const profile = workspace.bootstrap!.profile;
  const [profileOpen, setProfileOpen] = useState(false);
  const [addRivalOpen, setAddRivalOpen] = useState(false);
  const [openRival, setOpenRival] = useState<RivalListItem | null>(null);
  const [openRound, setOpenRound] = useState<RoundListItem | null>(null);
  const openRoundId = useRoundSource(
    workspace.accountId,
    setOpenRound,
    props.authenticated,
  );
  const onAvatarChanged = () => refreshAvatar(props.pageState.reload);
  return (
    <div className="fr-stage">
      <DesktopLanding />
      <div className="fr-app fr-approved-app" data-page={route.page}>
        <PageBackground page={shownPage(route, props.pageState.data)} />
        <ApprovedNavigation
          route={route}
          localOnly={!props.authenticated}
          roundOpen={
            workspace.snapshot !== null && isRoundOpen(workspace.snapshot.state)
          }
        />
        <ShellProgress pending={props.pageState.pending} />
        <ErrorBoundary area={`page:${route.page}`} resetKey={route.page}>
          <main className="fr-main" id={route.page}>
            <ShellBanners {...props} />
            <PageContent
              {...props}
              profileOpen={profileOpen}
              onProfileOpenChange={setProfileOpen}
              onAvatarChanged={onAvatarChanged}
              onAddRival={() => setAddRivalOpen(true)}
              onOpenRival={setOpenRival}
              onOpenRound={setOpenRound}
              onOpenRoundId={openRoundId}
            />
          </main>
          <Suspense
            fallback={
              <p className="fr-empty" role="status">
                Opening…
              </p>
            }
          >
            <DetailOverlays
              rival={openRival}
              round={openRound}
              profile={profile}
              route={route}
              onCloseRival={() => setOpenRival(null)}
              onCloseRound={() => setOpenRound(null)}
              onOpenRoundId={openRoundId}
              onResultUpdated={props.pageState.reload}
              onRoundChanged={() => {
                setOpenRound(null);
                props.pageState.reload();
              }}
            />
            <AccountOverlays
              shell={props}
              profileOpen={profileOpen}
              addRivalOpen={addRivalOpen}
              avatarSrc={null}
              onCloseProfile={() => setProfileOpen(false)}
              onCloseAddRival={() => setAddRivalOpen(false)}
              onAvatarChanged={onAvatarChanged}
            />
          </Suspense>
        </ErrorBoundary>
        <AdminViewChoice route={route} />
        <Toaster />
      </div>
    </div>
  );
}

function refreshAvatar(reload: () => void) {
  announceAvatarChanged();
  reload();
}
function AccountOverlays(props: {
  shell: AppShellProps;
  profileOpen: boolean;
  addRivalOpen: boolean;
  avatarSrc: string | null;
  onCloseProfile(): void;
  onCloseAddRival(): void;
  onAvatarChanged(): void;
}): React.JSX.Element {
  const { shell } = props;
  const view = useAdminView();
  return (
    <>
      {props.profileOpen ? (
        <ProfileCardOverlay
          open
          avatarSrc={props.avatarSrc}
          role={view.showAdminTools ? "admin" : "user"}
          onClose={props.onCloseProfile}
          onNavigate={shell.route.navigate}
          onSignOut={shell.onSignOut}
          onSaved={shell.pageState.reload}
          onAvatarChanged={props.onAvatarChanged}
        />
      ) : null}
      {props.addRivalOpen ? (
        <AddRivalDialog
          open
          onClose={props.onCloseAddRival}
          onChanged={shell.pageState.reload}
        />
      ) : null}
    </>
  );
}

/** The approved frame scrolls its main element independently of the window. */
function usePageScrollReset(page: ClientPageKey): void {
  useLayoutEffect(() => {
    document
      .querySelector(".fr-approved-app > .fr-main")
      ?.scrollTo({ top: 0, behavior: "instant" });
  }, [page]);
}

function ShellProgress({
  pending,
}: {
  pending: boolean;
}): React.JSX.Element | null {
  return pending ? (
    <div className="fr-progress" role="progressbar" aria-label="Loading" />
  ) : null;
}

/** The rival profile and round detail overlays opened from any page. */
function DetailOverlays(props: {
  rival: RivalListItem | null;
  round: RoundListItem | null;
  profile: BootstrapPayload["profile"];
  route: ProductRoute;
  onCloseRival(): void;
  onCloseRound(): void;
  onRoundChanged(): void;
  onOpenRoundId(id: string): void;
  onResultUpdated(): void;
}): React.JSX.Element {
  const { profile, route } = props;
  return (
    <>
      {props.rival ? (
        <RivalryProfileOverlay
          rival={props.rival}
          selfName={profile.displayName}
          selfHandicap={profile.handicapDisplay ?? null}
          onClose={props.onCloseRival}
          onOpenRoundId={(id) => {
            props.onCloseRival();
            props.onOpenRoundId(id);
          }}
          onOpenInsights={(id) => {
            props.onCloseRival();
            route.navigate("rivalry");
            window.history.replaceState(
              null,
              "",
              `${route.pathFor("rivalry")}?relation=${encodeURIComponent(id)}`,
            );
          }}
          onViewRounds={() => {
            props.onCloseRival();
            route.navigate("rounds");
          }}
        />
      ) : null}
      {props.round ? (
        <RoundDetailDialog
          round={props.round}
          selfName={profile.displayName}
          onClose={props.onCloseRound}
          onOpenRivalry={() => {
            props.onCloseRound();
            route.navigate("rivalry");
          }}
          onReviewRequested={props.onRoundChanged}
          onResultUpdated={props.onResultUpdated}
        />
      ) : null}
    </>
  );
}

function ShellBanners(props: AppShellProps): React.JSX.Element | null {
  const banners: React.JSX.Element[] = [];
  if (props.requiresAuthentication) {
    banners.push(
      <aside className="fr-banner" role="status" key="auth">
        <span>Sign in again to continue with your account.</span>
        <Button variant="gold" size="sm" onClick={props.onAuthenticate}>
          Sign in
        </Button>
      </aside>,
    );
  }
  if (props.pwaStatus.updateReady && props.pwaStatus.updateSafe) {
    banners.push(
      <aside className="fr-banner" role="status" key="update">
        <span>{props.pwaStatus.updateMessage}</span>
        {props.pwaStatus.updateSafe ? (
          <Button
            variant="gold"
            size="sm"
            onClick={() => void props.pwaStatus.activateUpdate()}
          >
            Update now
          </Button>
        ) : null}
      </aside>,
    );
  }
  return banners.length ? <>{banners}</> : null;
}

/**
 * The page whose content is on screen. While the next page loads, the
 * previous one stays visible, so its background must stay too; the two
 * swap together in one cross-fade when the data arrives.
 */
function shownPage(
  route: ProductRoute,
  data: ProductPageState["data"],
): ClientPageKey {
  if (route.page === "more" || !data) return route.page;
  if (data.page === "rivals") return route.page;
  return data.page;
}
